import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const cleanupScrollLock = () => {
  const body = document.body;
  const root = document.documentElement;
  const props = [
    "overflow",
    "padding-right",
    "position",
    "top",
    "left",
    "right",
    "width",
    "height",
    "touch-action",
    "overscroll-behavior",
  ];
  props.forEach((prop) => {
    body.style.removeProperty(prop);
    root.style.removeProperty(prop);
  });
  body.classList.add("app-shell-active");
};

const lockAppScroll = () => {
  document.body.classList.add("dialog-open");
  const appScroll = document.querySelector(".app-scroll");
  if (appScroll) {
    if (appScroll.dataset.scrollTop === undefined) {
      appScroll.dataset.scrollTop = String(appScroll.scrollTop || 0);
    }
    appScroll.style.overflow = "hidden";
  } else {
    if (document.body.dataset.lockedScrollY === undefined) {
      document.body.dataset.lockedScrollY = String(window.scrollY || 0);
    }
    const lockedScrollY = parseFloat(document.body.dataset.lockedScrollY || "0");
    document.body.style.position = "fixed";
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.height = "100%";
    document.body.style.overflow = "hidden";
    document.documentElement.style.height = "100%";
  }
};

const unlockAppScroll = () => {
  document.body.classList.remove("dialog-open");
  const appScroll = document.querySelector(".app-scroll");
  if (appScroll && appScroll.dataset.scrollTop !== undefined) {
    const top = parseFloat(appScroll.dataset.scrollTop || "0");
    delete appScroll.dataset.scrollTop;
    appScroll.style.removeProperty("overflow");
    requestAnimationFrame(() => {
      appScroll.scrollTop = top;
    });
    setTimeout(() => {
      appScroll.scrollTop = top;
    }, 50);
  }
  if (document.body.dataset.lockedScrollY !== undefined) {
    const lockedScrollY = parseFloat(document.body.dataset.lockedScrollY || "0");
    delete document.body.dataset.lockedScrollY;
    requestAnimationFrame(() => {
      window.scrollTo(0, lockedScrollY);
    });
    setTimeout(() => {
      window.scrollTo(0, lockedScrollY);
    }, 50);
  }
};

const Dialog = ({ onOpenChange, open, defaultOpen, modal = false, ...props }) => {
  React.useEffect(() => {
    if (open) {
      lockAppScroll();
    } else if (open === false) {
      unlockAppScroll();
      cleanupScrollLock();
    }
  }, [open]);

  React.useEffect(() => {
    if (defaultOpen) {
      lockAppScroll();
      return () => {
        unlockAppScroll();
        cleanupScrollLock();
      };
    }
    return undefined;
  }, [defaultOpen]);

  const handleOpenChange = (open) => {
    if (open) {
      lockAppScroll();
    } else {
      unlockAppScroll();
    }
    if (!open) {
      requestAnimationFrame(cleanupScrollLock);
      setTimeout(cleanupScrollLock, 350);
    }
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  return (
    <DialogPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      modal={modal}
      onOpenChange={handleOpenChange}
      {...props}
    />
  );
};

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[9999] bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[10000] grid w-full max-w-lg max-h-[calc(100svh-2rem)] max-h-[calc(100dvh-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg duration-200 [-webkit-overflow-scrolling:touch] overscroll-contain data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
